import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Layout from './components/Layout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import SolicitarReset from './pages/SolicitarReset';
import ResetPassword  from './pages/ResetPassword';
import SeleccionRol   from './pages/registro/SeleccionRol';
import RegistroVeci   from './pages/registro/RegistroVeci';
import RegistroNegocio from './pages/registro/RegistroNegocio';
import VistaProyecto  from './pages/VistaProyecto';
import { useApp } from './context/AppContext';

// Negocio
import PanelNegocio from './pages/negocio/PanelNegocio';
import PedidosNegocio from './pages/negocio/PedidosNegocio';
import CatalogoNegocio from './pages/negocio/CatalogoNegocio';
import VecinosFiadoNegocio from './pages/negocio/VecinosFiadoNegocio';
import CobrosNegocio from './pages/negocio/CobrosNegocio';
import ReportesNegocio from './pages/negocio/ReportesNegocio';
import PerfilNegocio from './pages/negocio/PerfilNegocio';
import MensajesNegocio from './pages/negocio/MensajesNegocio';

// Veci
import DirectorioVeci from './pages/veci/DirectorioVeci';
import DetalleNegocioVeci from './pages/veci/DetalleNegocioVeci';
import CarritoVeci from './pages/veci/CarritoVeci';
import PedidosVeci from './pages/veci/PedidosVeci';
import CuentaVeci from './pages/veci/CuentaVeci';
import MensajesVeci from './pages/veci/MensajesVeci';

// Domiciliario
import DomiciliarioPanel from './pages/domiciliario/DomiciliarioPanel';

// Admin (superadmin)
import AdminLayout    from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsuarios  from './pages/admin/AdminUsuarios';
import AdminNegocios  from './pages/admin/AdminNegocios';
import AdminNegocioValidacion from './pages/admin/AdminNegocioValidacion';
import AdminValidacionPendientes from './pages/admin/AdminValidacionPendientes';
import AdminVecis     from './pages/admin/AdminVecis';
import AdminGuias     from './pages/admin/AdminGuias';
import AdminModeracion from './pages/admin/AdminModeracion';
import AdminTiendasRecomendadas from './pages/admin/AdminTiendasRecomendadas';
import AdminGuiaSupervision from './pages/admin/AdminGuiaSupervision';
import AdminConfiguracion from './pages/admin/AdminConfiguracion';
import AdminFacturasSaaS  from './pages/admin/AdminFacturasSaaS';
import AdminSuscripciones from './pages/admin/AdminSuscripciones';
import AdminLiquidaciones from './pages/admin/AdminLiquidaciones';
import AdminResultados    from './pages/admin/AdminResultados';
import AdminAuditoria from './pages/admin/AdminAuditoria';
import AdminSecretos  from './pages/admin/AdminSecretos';
import AdminProyecto  from './pages/admin/AdminProyecto';
import AdminSetup     from './pages/admin/AdminSetup';

// Panel Guía
import GuiaLayout        from './pages/guia/GuiaLayout';
import GuiaPanel         from './pages/guia/GuiaPanel';
import GuiaAfiliados     from './pages/guia/GuiaAfiliados';
import GuiaMapa          from './pages/guia/GuiaMapa';
import GuiaComisiones    from './pages/guia/GuiaComisiones';
import GuiaLiquidaciones from './pages/guia/GuiaLiquidaciones';
import GuiaPerfil        from './pages/guia/GuiaPerfil';

function RutaProtegida({ entidadRequerida, children }) {
  const { sesionBackend, cargandoSesion } = useApp();
  if (cargandoSesion) return null;
  if (!sesionBackend) return <Navigate to="/login" replace />;
  if (sesionBackend.entidadTipo !== entidadRequerida) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Protege las rutas del panel admin: requiere sesión backend con rol SUPERADMIN.
 * Mientras se hidrata la sesión al montar, muestra nada (evita redirect prematuro).
 */
function RutaSuperAdmin({ children }) {
  const { sesionBackend, cargandoSesion } = useApp();
  if (cargandoSesion) return null;
  if (!sesionBackend) return <Navigate to="/login" replace />;
  if (!sesionBackend.roles?.includes('SUPERADMIN')) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Protege las rutas del panel Guía: requiere sesión backend con rol GUIA.
 */
function RutaGuia({ children }) {
  const { sesionBackend, cargandoSesion } = useApp();
  if (cargandoSesion) return null;
  if (!sesionBackend) return <Navigate to="/login" replace />;
  if (!sesionBackend.roles?.includes('GUIA')) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/solicitar-reset" element={<SolicitarReset />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/registro"         element={<SeleccionRol />} />
      <Route path="/registro/veci"    element={<RegistroVeci />} />
      <Route path="/registro/negocio" element={<RegistroNegocio />} />

      {/* Setup inicial: sin protección, para poder crear el primer SuperAdmin */}
      <Route path="/admin/setup" element={<AdminSetup />} />

      {/* Panel admin — solo SUPERADMIN */}
      <Route
        element={
          <RutaSuperAdmin>
            <AdminLayout />
          </RutaSuperAdmin>
        }
      >
        <Route path="/admin"          element={<AdminDashboard />} />
        <Route path="/admin/usuarios" element={<AdminUsuarios />} />
        <Route path="/admin/negocios" element={<AdminNegocios />} />
        <Route path="/admin/validacion" element={<AdminValidacionPendientes />} />
        <Route path="/admin/negocios/:empresaId/validar" element={<AdminNegocioValidacion />} />
        <Route path="/admin/vecis"    element={<AdminVecis />} />
        <Route path="/admin/guias"    element={<AdminGuias />} />
        <Route path="/admin/moderacion" element={<AdminModeracion />} />
        <Route path="/admin/recomendaciones" element={<AdminTiendasRecomendadas />} />
        <Route path="/admin/guias/:id/supervision" element={<AdminGuiaSupervision />} />
        <Route path="/admin/suscripciones" element={<AdminSuscripciones />} />
        <Route path="/admin/facturas" element={<AdminFacturasSaaS />} />
        <Route path="/admin/liquidaciones" element={<AdminLiquidaciones />} />
        <Route path="/admin/resultados" element={<AdminResultados />} />
        <Route path="/admin/configuracion" element={<AdminConfiguracion />} />
        <Route path="/admin/auditoria" element={<AdminAuditoria />} />
        <Route path="/admin/secretos" element={<AdminSecretos />} />
        <Route path="/admin/proyecto" element={<AdminProyecto />} />
      </Route>

      {/* Panel Guía — solo rol GUIA */}
      <Route
        element={
          <RutaGuia>
            <GuiaLayout />
          </RutaGuia>
        }
      >
        <Route path="/guia"               element={<GuiaPanel />} />
        <Route path="/guia/afiliados"     element={<GuiaAfiliados />} />
        <Route path="/guia/mapa"          element={<GuiaMapa />} />
        <Route path="/guia/comisiones"    element={<GuiaComisiones />} />
        <Route path="/guia/liquidaciones" element={<GuiaLiquidaciones />} />
        <Route path="/guia/perfil"        element={<GuiaPerfil />} />
      </Route>

      <Route
        element={
          <RutaProtegida entidadRequerida="NEGOCIO">
            <Layout />
          </RutaProtegida>
        }
      >
        <Route path="/negocio" element={<PanelNegocio />} />
        <Route path="/negocio/pedidos" element={<PedidosNegocio />} />
        <Route path="/negocio/catalogo" element={<CatalogoNegocio />} />
        <Route path="/negocio/vecinos" element={<VecinosFiadoNegocio />} />
        <Route path="/negocio/vecinos/:veciId" element={<VecinosFiadoNegocio />} />
        <Route path="/negocio/cobros" element={<CobrosNegocio />} />
        <Route path="/negocio/reportes" element={<ReportesNegocio />} />
        <Route path="/negocio/perfil" element={<PerfilNegocio />} />
        <Route path="/negocio/mensajes" element={<MensajesNegocio />} />
        <Route path="/negocio/mensajes/:veciId" element={<MensajesNegocio />} />
      </Route>

      <Route
        element={
          <RutaProtegida entidadRequerida="VECI">
            <Layout />
          </RutaProtegida>
        }
      >
        <Route path="/veci" element={<DirectorioVeci />} />
        <Route path="/veci/negocio/:negocioId" element={<DetalleNegocioVeci />} />
        <Route path="/veci/carrito" element={<CarritoVeci />} />
        <Route path="/veci/pedidos" element={<PedidosVeci />} />
        <Route path="/veci/cuenta" element={<CuentaVeci />} />
        <Route path="/veci/mensajes" element={<MensajesVeci />} />
        <Route path="/veci/mensajes/:negocioId" element={<MensajesVeci />} />
      </Route>

      <Route
        path="/domiciliario"
        element={
          <RutaProtegida entidadRequerida="DOMICILIARIO">
            <DomiciliarioPanel />
          </RutaProtegida>
        }
      />

      <Route
        path="/proyecto"
        element={
          <RutaProtegida entidadRequerida="INVERSIONISTA">
            <VistaProyecto />
          </RutaProtegida>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
